import { Feature } from 'geojson';
import _ from 'lodash';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { PolygonGeometry } from 'uf/base/geometry';
import { addRumError } from 'uf/datadog';
import * as filterActionCreators from 'uf/explore/actions/filters';
import { getMapMode } from 'uf/explore/selectors/map';
import { events, logEvent } from 'uf/logging';
import { ProjectId } from 'uf/projects';

export function useMapSelection(
  projectId: ProjectId,
  activeLayerId: string,
  selectedGeoKeys: string[],
  clearSelectionFilters: typeof filterActionCreators.clearSelectionFilters,
  updateGeometryFilter: typeof filterActionCreators.updateGeometryFilter,
  updatePointFilter: typeof filterActionCreators.updatePointFilter,
) {
  const ufState: any = useSelector(state => state);
  const mapMode = getMapMode(ufState);
  const onPolygonSelection = useCallback(
    (polygon: PolygonGeometry, add: boolean) => {
      logEvent(events.MAP_TOOL_SELECTION, {
        projectId,
        activeLayerId,
        boundingBox: polygon,
        add,
        tool: mapMode,
      });
      handlePolygonSelection(
        projectId,
        activeLayerId,
        add,
        clearSelectionFilters,
        updateGeometryFilter,
        polygon,
      );
    },
    [
      projectId,
      activeLayerId,
      mapMode,
      clearSelectionFilters,
      updateGeometryFilter,
    ],
  );
  const onPointSelection = useCallback(
    (features: Feature[], add: boolean) => {
      logEvent(events.MAP_TOOL_SELECTION, {
        projectId,
        activeLayerId,
        properties: features?.[0]?.properties,
        add,
        tool: mapMode,
      });
      return handlePointSelection(
        projectId,
        activeLayerId,
        selectedGeoKeys,
        features,
        add,
        clearSelectionFilters,
        updatePointFilter,
      );
    },
    [
      projectId,
      activeLayerId,
      selectedGeoKeys,
      mapMode,
      clearSelectionFilters,
      updatePointFilter,
    ],
  );
  return { onPolygonSelection, onPointSelection };
}

function handlePolygonSelection(
  projectId: ProjectId,
  activeLayerId: string,
  add: boolean,
  clearSelectionFilters: typeof filterActionCreators.clearSelectionFilters,
  updateGeometryFilter: typeof filterActionCreators.updateGeometryFilter,
  polygon: PolygonGeometry,
) {
  if (!add) {
    clearSelectionFilters(projectId, activeLayerId);
  }
  updateGeometryFilter(projectId, activeLayerId, 'polygon', polygon);
}

function handlePointSelection(
  projectId: ProjectId,
  activeLayerId: string,
  currentGeoKeys: string[],
  features: Feature[],
  add: boolean,
  clearSelectionFilters: typeof filterActionCreators.clearSelectionFilters,
  updatePointFilter: typeof filterActionCreators.updatePointFilter,
) {
  try {
    const pointIds = features.map(({ properties }) => properties.geometry_key);
    if (_.isEmpty(pointIds)) {
      return;
    }
    if (!add) {
      clearSelectionFilters(projectId, activeLayerId);
    }
    updatePointFilter(projectId, activeLayerId, pointIds, currentGeoKeys, add);
  } catch (e) {
    addRumError(e, {
      featureCount: features?.length,
    });
  }
}
